import { useCallback, useEffect, useState } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  CheckCircle2,
  XCircle,
  RefreshCw,
  Plus,
  Trash2,
  Send,
  Bell,
  Eye,
  EyeOff,
  Hash,
  User,
  Users,
  X,
} from 'lucide-react';
import {
  notificationChannelsApi,
  userSlackMappingApi,
  type NotificationChannel,
  type UpsertNotificationChannel,
  type SlackLookupResponse,
  type UserSlackMapping,
} from '@/lib/api';

// ── Channel metadata ─────────────────────────────────────────

const CHANNEL_META: Record<
  string,
  {
    label: string;
    icon: string;
    color: string;
    bgColor: string;
    fields: ('webhook_url' | 'bot_token' | 'chat_id')[];
    fieldLabels: Record<string, string>;
    fieldPlaceholders: Record<string, string>;
    helpText: string;
  }
> = {
  slack: {
    label: 'Slack',
    icon: '💬',
    color: 'text-purple-600 dark:text-purple-400',
    bgColor: 'bg-purple-50 dark:bg-purple-950/30 border-purple-200 dark:border-purple-800',
    fields: ['bot_token', 'chat_id'],
    fieldLabels: {
      bot_token: 'Bot Token',
      chat_id: 'Channel ID',
    },
    fieldPlaceholders: {
      bot_token: 'xoxb-...',
      chat_id: 'C0123456789',
    },
    helpText:
      'Bot Token (xoxb-) for sending messages. Channel ID for target channel. Invite the bot to the channel first.',
  },
  telegram: {
    label: 'Telegram',
    icon: '✈️',
    color: 'text-blue-600 dark:text-blue-400',
    bgColor: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-800',
    fields: ['bot_token', 'chat_id'],
    fieldLabels: { bot_token: 'Bot Token', chat_id: 'Chat ID' },
    fieldPlaceholders: {
      bot_token: '123456:ABC-DEF1234ghIkl-zyx57W2v...',
      chat_id: '-1001234567890 or @channel_name',
    },
    helpText:
      'Create a bot with @BotFather on Telegram. Use @userinfobot or /getUpdates to find the Chat ID.',
  },
  discord: {
    label: 'Discord',
    icon: '🎮',
    color: 'text-indigo-600 dark:text-indigo-400',
    bgColor: 'bg-indigo-50 dark:bg-indigo-950/30 border-indigo-200 dark:border-indigo-800',
    fields: ['webhook_url'],
    fieldLabels: { webhook_url: 'Webhook URL' },
    fieldPlaceholders: {
      webhook_url: 'https://discord.com/api/webhooks/1234567890/abcdef...',
    },
    helpText:
      'In your Discord channel: Settings → Integrations → Webhooks → New Webhook → Copy Webhook URL.',
  },
};

const CHANNEL_TYPES = ['slack', 'telegram', 'discord'] as const;

// ── Types ────────────────────────────────────────────────────

interface EditingChannel {
  id?: number;
  channel_type: string;
  target_type: 'group' | 'individual';
  target_name: string;
  webhook_url: string;
  bot_token: string;
  chat_id: string;
  enabled: boolean;
  notify_status_change: boolean;
  notify_errors: boolean;
}

const emptyChannel = (
  channelType: string = 'slack'
): EditingChannel => ({
  channel_type: channelType,
  target_type: 'group',
  target_name: '',
  webhook_url: '',
  bot_token: '',
  chat_id: '',
  enabled: true,
  notify_status_change: true,
  notify_errors: true,
});

// ── Component ────────────────────────────────────────────────

export function NotificationSettings() {
  const [channels, setChannels] = useState<NotificationChannel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Dialog state
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState<EditingChannel>(emptyChannel());
  const [saving, setSaving] = useState(false);
  const [testingId, setTestingId] = useState<number | null>(null);
  const [testResult, setTestResult] = useState<{
    id: number;
    success: boolean;
    message: string;
  } | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  // Global Slack Bot Token
  const [slackBotToken, setSlackBotToken] = useState('');
  const [slackBotTokenSaved, setSlackBotTokenSaved] = useState('');
  const [showSlackBotToken, setShowSlackBotToken] = useState(false);
  const [savingBotToken, setSavingBotToken] = useState(false);

  // Slack lookup state
  const [slackLookup, setSlackLookup] = useState<SlackLookupResponse | null>(
    null
  );
  const [slackLookupLoading, setSlackLookupLoading] = useState(false);
  const [slackLookupError, setSlackLookupError] = useState<string | null>(null);
  const [slackSearch, setSlackSearch] = useState('');
  const [showSlackDropdown, setShowSlackDropdown] = useState(false);

  // Load channels
  const loadChannels = useCallback(async () => {
    try {
      setLoading(true);
      const data = await notificationChannelsApi.list();
      setChannels(data);
      // Auto-populate global Slack Bot Token from any existing Slack channel
      const existingToken = data.find(
        (ch) => ch.channel_type === 'slack' && ch.bot_token
      )?.bot_token;
      if (existingToken) {
        setSlackBotToken(existingToken);
        setSlackBotTokenSaved(existingToken);
      }
    } catch (err) {
      setError('Failed to load notification channels');
      console.error('Failed to load channels:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadChannels();
  }, [loadChannels]);

  // Handlers
  const handleAdd = () => {
    const channel = emptyChannel();
    // Auto-fill bot_token from global setting for Slack
    if (slackBotToken) channel.bot_token = slackBotToken;
    setEditing(channel);
    setShowDialog(true);
    setTestResult(null);
    setSlackLookup(null);
    setSlackLookupError(null);
    setSlackSearch('');
  };

  const handleEdit = (ch: NotificationChannel) => {
    setEditing({
      id: ch.id,
      channel_type: ch.channel_type,
      target_type: ch.target_type as 'group' | 'individual',
      target_name: ch.target_name,
      webhook_url: ch.webhook_url,
      bot_token: ch.bot_token,
      chat_id: ch.chat_id,
      enabled: ch.enabled !== 0,
      notify_status_change: ch.notify_status_change !== 0,
      notify_errors: ch.notify_errors !== 0,
    });
    setShowDialog(true);
    setTestResult(null);
    setSlackLookup(null);
    setSlackLookupError(null);
    setSlackSearch('');
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const data: UpsertNotificationChannel = {
        target_type: editing.target_type,
        target_name: editing.target_name,
        webhook_url: editing.webhook_url,
        bot_token: editing.bot_token,
        chat_id: editing.chat_id,
        enabled: editing.enabled,
        notify_status_change: editing.notify_status_change,
        notify_errors: editing.notify_errors,
      };
      await notificationChannelsApi.upsert(editing.channel_type, data);
      setSuccess('Channel saved successfully');
      setTimeout(() => setSuccess(null), 3000);
      setShowDialog(false);
      await loadChannels();
    } catch (err) {
      setError('Failed to save channel');
      console.error('Failed to save channel:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (!confirm('Are you sure you want to delete this notification channel?'))
      return;
    try {
      await notificationChannelsApi.delete(id);
      setSuccess('Channel deleted');
      setTimeout(() => setSuccess(null), 3000);
      await loadChannels();
    } catch (err) {
      setError('Failed to delete channel');
      console.error('Failed to delete channel:', err);
    }
  };

  const handleTest = async (id: number) => {
    setTestingId(id);
    setTestResult(null);
    try {
      const result = await notificationChannelsApi.test(id);
      setTestResult({ id, ...result });
    } catch (err) {
      setTestResult({
        id,
        success: false,
        message: 'Failed to reach the server',
      });
    } finally {
      setTestingId(null);
    }
  };

  const handleToggle = async (ch: NotificationChannel) => {
    try {
      await notificationChannelsApi.upsert(ch.channel_type, {
        target_type: ch.target_type,
        target_name: ch.target_name,
        enabled: ch.enabled === 0,
      });
      await loadChannels();
    } catch (err) {
      setError('Failed to toggle channel');
    }
  };

  // Slack lookup handler
  const handleSlackLookup = async () => {
    if (!slackBotToken) {
      setSlackLookupError('Save a Slack Bot Token first (above)');
      return;
    }
    setSlackLookupLoading(true);
    setSlackLookupError(null);
    try {
      const data = await notificationChannelsApi.slackLookup(
        slackBotToken
      );
      setSlackLookup(data);
      setSlackSearch('');
    } catch (err) {
      setSlackLookupError(
        err instanceof Error ? err.message : 'Failed to fetch Slack data'
      );
    } finally {
      setSlackLookupLoading(false);
    }
  };

  // Filter Slack items based on search and target type
  const filteredSlackItems = (() => {
    if (!slackLookup) return [];
    const search = slackSearch.toLowerCase();
    if (editing.target_type === 'group') {
      return slackLookup.channels
        .filter(
          (ch) =>
            ch.name.toLowerCase().includes(search) ||
            ch.id.toLowerCase().includes(search)
        )
        .map((ch) => ({
          id: ch.id,
          display: `#${ch.name}`,
          detail: ch.is_private
            ? '🔒 Private'
            : ch.num_members != null
              ? `${ch.num_members} members`
              : '',
          targetName: `#${ch.name}`,
          chatId: ch.id,
        }));
    } else {
      return slackLookup.users
        .filter(
          (u) =>
            !u.is_bot &&
            (u.name.toLowerCase().includes(search) ||
              u.real_name.toLowerCase().includes(search) ||
              u.id.toLowerCase().includes(search))
        )
        .map((u) => ({
          id: u.id,
          display: `@${u.name}`,
          detail: u.real_name,
          targetName: `@${u.name}`,
          chatId: u.id,
        }));
    }
  })();

  // Group channels by type for summary cards
  const channelsByType = CHANNEL_TYPES.reduce(
    (acc, type) => {
      acc[type] = channels.filter((ch) => ch.channel_type === type);
      return acc;
    },
    {} as Record<string, NotificationChannel[]>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-8 w-8 animate-spin" />
        <span className="ml-2">Loading notification channels...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {success && (
        <Alert variant="success">
          <AlertDescription className="font-medium">
            {success}
          </AlertDescription>
        </Alert>
      )}

      {/* ═══ Overview Cards ═══ */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {CHANNEL_TYPES.map((type) => {
          const meta = CHANNEL_META[type];
          const typeChannels = channelsByType[type] || [];
          const enabledCount = typeChannels.filter(
            (c) => c.enabled !== 0
          ).length;

          return (
            <Card
              key={type}
              className={`transition-all ${meta.bgColor}`}
            >
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center justify-between text-base">
                  <span className="flex items-center gap-2">
                    <span className="text-xl">{meta.icon}</span>
                    <span className={meta.color}>{meta.label}</span>
                  </span>
                  {typeChannels.length > 0 ? (
                    <Badge
                      variant={enabledCount > 0 ? 'default' : 'secondary'}
                      className="text-xs"
                    >
                      {enabledCount}/{typeChannels.length} active
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">
                      Not configured
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="pt-0">
                {typeChannels.length > 0 ? (
                  <div className="space-y-1.5">
                    {typeChannels.map((ch) => (
                      <div
                        key={ch.id}
                        className="flex items-center justify-between text-xs"
                      >
                        <span className="flex items-center gap-1.5 truncate">
                          {ch.target_type === 'individual' ? (
                            <User className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <Users className="h-3 w-3 text-muted-foreground" />
                          )}
                          <span className="truncate">
                            {ch.target_name || '(default)'}
                          </span>
                        </span>
                        <span
                          className={`h-2 w-2 rounded-full ${ch.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
                        />
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {meta.helpText.split('.')[0]}.
                  </p>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* ═══ Slack Bot Token Configuration ═══ */}
      <Card className="border-purple-200 dark:border-purple-800">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <span className="text-xl">💬</span>
            <span className="text-purple-600 dark:text-purple-400">Slack Bot Token</span>
            {slackBotTokenSaved && (
              <Badge variant="default" className="text-xs gap-1">
                <CheckCircle2 className="h-3 w-3" />
                Connected
              </Badge>
            )}
          </CardTitle>
          <CardDescription>
            Enter your Slack Bot Token once here. It will be used for all Slack channels, DM notifications, and channel lookup.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Input
                type={showSlackBotToken ? 'text' : 'password'}
                placeholder="xoxb-..."
                value={slackBotToken}
                onChange={(e) => setSlackBotToken(e.target.value)}
                className="pr-10"
              />
              <button
                type="button"
                onClick={() => setShowSlackBotToken((s) => !s)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                {showSlackBotToken ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
            <Button
              size="sm"
              className="h-10 gap-1.5"
              disabled={savingBotToken || slackBotToken === slackBotTokenSaved}
              onClick={async () => {
                setSavingBotToken(true);
                try {
                  // Update bot_token on all existing Slack channels
                  const slackChannels = channels.filter((ch) => ch.channel_type === 'slack');
                  for (const ch of slackChannels) {
                    await notificationChannelsApi.upsert(ch.channel_type, {
                      target_type: ch.target_type,
                      target_name: ch.target_name,
                      bot_token: slackBotToken,
                      webhook_url: ch.webhook_url,
                      chat_id: ch.chat_id,
                      enabled: ch.enabled !== 0,
                      notify_status_change: ch.notify_status_change !== 0,
                      notify_errors: ch.notify_errors !== 0,
                    });
                  }
                  setSlackBotTokenSaved(slackBotToken);
                  setSuccess('Slack Bot Token saved');
                  setTimeout(() => setSuccess(null), 3000);
                  await loadChannels();
                } catch {
                  setError('Failed to save Bot Token');
                } finally {
                  setSavingBotToken(false);
                }
              }}
            >
              {savingBotToken ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <CheckCircle2 className="h-3.5 w-3.5" />}
              Save
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Create a Slack App → Bot User → Install to workspace → Copy the <code className="bg-muted px-1 rounded">xoxb-</code> token. Bot needs <code className="bg-muted px-1 rounded">chat:write</code>, <code className="bg-muted px-1 rounded">channels:read</code>, <code className="bg-muted px-1 rounded">users:read</code> scopes.
          </p>
        </CardContent>
      </Card>

      {/* ═══ Channels Table ═══ */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Channels
              </CardTitle>
              <CardDescription>
                Configure where notifications are sent. Add group channels for
                team-wide alerts or individual targets for personal
                notifications.
              </CardDescription>
            </div>
            <Button onClick={handleAdd} className="gap-2" size="sm">
              <Plus className="h-4 w-4" />
              Add Channel
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {channels.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Bell className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="font-medium">No notification channels configured</p>
              <p className="text-sm mt-1">
                Add a Slack, Telegram, or Discord channel to receive
                notifications about task status changes and errors.
              </p>
              <Button
                onClick={handleAdd}
                variant="outline"
                className="mt-4 gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Your First Channel
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left">
                    <th className="py-2.5 px-3 font-medium text-muted-foreground w-8">
                      On
                    </th>
                    <th className="py-2.5 px-3 font-medium text-muted-foreground">
                      Channel
                    </th>
                    <th className="py-2.5 px-3 font-medium text-muted-foreground">
                      Target
                    </th>
                    <th className="py-2.5 px-3 font-medium text-muted-foreground">
                      Notify on
                    </th>
                    <th className="py-2.5 px-3 font-medium text-muted-foreground text-right">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {channels.map((ch) => {
                    const meta = CHANNEL_META[ch.channel_type];
                    return (
                      <tr
                        key={ch.id}
                        className="border-b last:border-0 hover:bg-muted/50 transition-colors cursor-pointer"
                        onClick={() => handleEdit(ch)}
                      >
                        <td
                          className="py-2.5 px-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <Switch
                            checked={ch.enabled !== 0}
                            onCheckedChange={() => handleToggle(ch)}
                          />
                        </td>
                        <td className="py-2.5 px-3">
                          <span className="flex items-center gap-2">
                            <span>{meta?.icon}</span>
                            <span className={`font-medium ${meta?.color}`}>
                              {meta?.label}
                            </span>
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          <span className="flex items-center gap-2">
                            <Badge
                              variant={
                                ch.target_type === 'individual'
                                  ? 'default'
                                  : 'secondary'
                              }
                              className="text-xs gap-1"
                            >
                              {ch.target_type === 'individual' ? (
                                <User className="h-3 w-3" />
                              ) : (
                                <Hash className="h-3 w-3" />
                              )}
                              {ch.target_type === 'individual'
                                ? 'Individual'
                                : 'Group'}
                            </Badge>
                            <span className="text-xs text-muted-foreground truncate max-w-[150px]">
                              {ch.target_name || '—'}
                            </span>
                          </span>
                        </td>
                        <td className="py-2.5 px-3">
                          <div className="flex gap-1.5">
                            {ch.notify_status_change !== 0 && (
                              <Badge
                                variant="outline"
                                className="text-xs gap-1"
                              >
                                <RefreshCw className="h-3 w-3" />
                                Status
                              </Badge>
                            )}
                            {ch.notify_errors !== 0 && (
                              <Badge
                                variant="outline"
                                className="text-xs gap-1"
                              >
                                <XCircle className="h-3 w-3" />
                                Errors
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td
                          className="py-2.5 px-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <div className="flex items-center justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleTest(ch.id)}
                              disabled={testingId === ch.id}
                              className="h-7 px-2 gap-1 text-xs"
                            >
                              {testingId === ch.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : (
                                <Send className="h-3 w-3" />
                              )}
                              Test
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(ch.id)}
                              className="h-7 px-2 text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          {testResult?.id === ch.id && (
                            <div
                              className={`flex items-center gap-1 mt-1 text-xs ${testResult.success ? 'text-green-600' : 'text-destructive'}`}
                            >
                              {testResult.success ? (
                                <CheckCircle2 className="h-3 w-3" />
                              ) : (
                                <XCircle className="h-3 w-3" />
                              )}
                              <span className="truncate max-w-[120px]">
                                {testResult.message}
                              </span>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* ═══ Add/Edit Dialog (overlay) ═══ */}
      {showDialog && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-background rounded-lg shadow-xl w-full max-w-lg mx-4 max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">
                {editing.id
                  ? 'Edit Notification Channel'
                  : 'Add Notification Channel'}
              </h3>
              <button
                onClick={() => setShowDialog(false)}
                className="text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Body */}
            <div className="px-6 py-4 space-y-5">
              {/* Channel Type */}
              <div className="space-y-2">
                <Label>Channel Type</Label>
                <div className="grid grid-cols-3 gap-2">
                  {CHANNEL_TYPES.map((type) => {
                    const meta = CHANNEL_META[type];
                    const isSelected = editing.channel_type === type;
                    return (
                      <button
                        key={type}
                        onClick={() =>
                          setEditing((e) => ({
                            ...e,
                            channel_type: type,
                            webhook_url: '',
                            bot_token: '',
                            chat_id: '',
                          }))
                        }
                        disabled={!!editing.id}
                        className={`flex items-center gap-2 px-3 py-2.5 rounded-md border text-sm font-medium transition-all ${
                          isSelected
                            ? `${meta.bgColor} ring-2 ring-primary`
                            : 'border-border hover:border-primary/50'
                        } ${editing.id ? 'opacity-60 cursor-not-allowed' : ''}`}
                      >
                        <span className="text-lg">{meta.icon}</span>
                        <span className={isSelected ? meta.color : ''}>
                          {meta.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Target Info */}
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-md border bg-accent/30 text-sm">
                <Hash className="h-4 w-4 text-muted-foreground" />
                <div>
                  <div className="font-medium">Group Channel</div>
                  <div className="text-xs text-muted-foreground">
                    Team-wide notifications
                  </div>
                </div>
              </div>
              {editing.channel_type === 'slack' && (
                <p className="text-xs text-muted-foreground bg-blue-50 dark:bg-blue-950/20 px-3 py-2 rounded-md border border-blue-200 dark:border-blue-800">
                  💡 Bot Token is configured above. For personal DMs, use the <strong>User → Slack Mapping</strong> section below.
                </p>
              )}

              {/* Target Name — with Slack Lookup */}
              <div className="space-y-2">
                <Label htmlFor="target-name">
                  Channel Name
                </Label>

                {/* Slack: Show "Fetch from Slack" button */}
                {editing.channel_type === 'slack' && !slackLookup && (
                  <div className="flex gap-2">
                    <Input
                      id="target-name"
                      placeholder="#dev-team, #alerts, etc."
                      value={editing.target_name}
                      onChange={(e) =>
                        setEditing((prev) => ({
                          ...prev,
                          target_name: e.target.value,
                        }))
                      }
                      className="flex-1"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleSlackLookup}
                      disabled={slackLookupLoading || !slackBotToken}
                      className="gap-1.5 shrink-0 h-10"
                    >
                      {slackLookupLoading ? (
                        <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-3.5 w-3.5" />
                      )}
                      Fetch from Slack
                    </Button>
                  </div>
                )}

                {/* Slack: Show searchable dropdown when data is loaded */}
                {editing.channel_type === 'slack' && slackLookup && (
                  <div className="relative">
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          id="target-name"
                          placeholder="Search channels..."
                          value={showSlackDropdown ? slackSearch : editing.target_name}
                          onChange={(e) => {
                            setSlackSearch(e.target.value);
                            setShowSlackDropdown(true);
                            // Also update target_name for manual input
                            if (!showSlackDropdown) {
                              setEditing((prev) => ({
                                ...prev,
                                target_name: e.target.value,
                              }));
                            }
                          }}
                          onFocus={() => setShowSlackDropdown(true)}
                        />
                        {editing.target_name && !showSlackDropdown && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditing((prev) => ({
                                ...prev,
                                target_name: '',
                                chat_id: '',
                              }));
                              setShowSlackDropdown(true);
                              setSlackSearch('');
                            }}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSlackLookup(null);
                          setSlackSearch('');
                          setShowSlackDropdown(false);
                        }}
                        className="h-10 px-2 text-muted-foreground"
                        title="Clear Slack data"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>

                    {/* Dropdown */}
                    {showSlackDropdown && (
                      <div className="absolute z-10 w-full mt-1 bg-background border rounded-md shadow-lg max-h-48 overflow-y-auto">
                        {filteredSlackItems.length === 0 ? (
                          <div className="px-3 py-2 text-sm text-muted-foreground">
                            {slackSearch
                              ? 'No matches found'
                              : 'No channels available'}
                          </div>
                        ) : (
                          filteredSlackItems.map((item) => (
                            <button
                              key={item.id}
                              type="button"
                              onClick={() => {
                                setEditing((prev) => ({
                                  ...prev,
                                  target_name: item.targetName,
                                  chat_id: item.chatId,
                                }));
                                setShowSlackDropdown(false);
                                setSlackSearch('');
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-accent transition-colors flex items-center justify-between text-sm"
                            >
                              <span className="font-medium truncate">
                                {item.display}
                              </span>
                              <span className="text-xs text-muted-foreground ml-2 shrink-0">
                                {item.detail}
                              </span>
                            </button>
                          ))
                        )}
                      </div>
                    )}

                    {/* Summary */}
                    <p className="text-xs text-muted-foreground mt-1">
                      Found {slackLookup.channels.length} channels,{' '}
                      {slackLookup.users.filter((u) => !u.is_bot).length} users
                    </p>
                  </div>
                )}

                {/* Non-Slack: regular input */}
                {editing.channel_type !== 'slack' && (
                  <Input
                    id="target-name"
                    placeholder="#channel-name, group, etc."
                    value={editing.target_name}
                    onChange={(e) =>
                      setEditing((prev) => ({
                        ...prev,
                        target_name: e.target.value,
                      }))
                    }
                  />
                )}

                {slackLookupError && (
                  <p className="text-xs text-destructive">
                    {slackLookupError}
                  </p>
                )}

                {!slackLookup && (
                  <p className="text-xs text-muted-foreground">
                    {editing.channel_type === 'slack'
                      ? 'Enter a name or use "Fetch from Slack" to browse available channels.'
                      : 'A display name to identify this channel in the list.'}
                  </p>
                )}
              </div>

              {/* Channel-specific fields (skip bot_token for Slack — already rendered above) */}
              {CHANNEL_META[editing.channel_type]?.fields
                .filter((field) => !(editing.channel_type === 'slack' && field === 'bot_token'))
                .map((field) => {
                const meta = CHANNEL_META[editing.channel_type];
                const isSecret =
                  field === 'bot_token' || field === 'webhook_url';
                const secretKey = `${editing.id ?? 'new'}-${field}`;

                return (
                  <div key={field} className="space-y-2">
                    <Label htmlFor={`field-${field}`}>
                      {meta.fieldLabels[field]}
                    </Label>
                    <div className="relative">
                      <Input
                        id={`field-${field}`}
                        type={
                          isSecret && !showSecrets[secretKey]
                            ? 'password'
                            : 'text'
                        }
                        placeholder={meta.fieldPlaceholders[field]}
                        value={editing[field]}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [field]: e.target.value,
                          }))
                        }
                        className={isSecret ? 'pr-10' : ''}
                      />
                      {isSecret && (
                        <button
                          type="button"
                          onClick={() =>
                            setShowSecrets((s) => ({
                              ...s,
                              [secretKey]: !s[secretKey],
                            }))
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                        >
                          {showSecrets[secretKey] ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}

              <p className="text-xs text-muted-foreground">
                {CHANNEL_META[editing.channel_type]?.helpText}
              </p>

              {/* Notification preferences */}
              <div className="space-y-3 pt-2 border-t">
                <Label className="text-sm font-medium">Notify on</Label>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm">Status changes</span>
                    <p className="text-xs text-muted-foreground">
                      When a task moves between statuses
                    </p>
                  </div>
                  <Switch
                    checked={editing.notify_status_change}
                    onCheckedChange={(v) =>
                      setEditing((e) => ({ ...e, notify_status_change: v }))
                    }
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <span className="text-sm">Errors</span>
                    <p className="text-xs text-muted-foreground">
                      Sync failures and system errors
                    </p>
                  </div>
                  <Switch
                    checked={editing.notify_errors}
                    onCheckedChange={(v) =>
                      setEditing((e) => ({ ...e, notify_errors: v }))
                    }
                  />
                </div>
              </div>

              {/* Enabled toggle */}
              <div className="flex items-center justify-between pt-2 border-t">
                <div>
                  <span className="text-sm font-medium">Enabled</span>
                  <p className="text-xs text-muted-foreground">
                    Turn this channel on or off
                  </p>
                </div>
                <Switch
                  checked={editing.enabled}
                  onCheckedChange={(v) =>
                    setEditing((e) => ({ ...e, enabled: v }))
                  }
                />
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-muted/30">
              <Button
                variant="outline"
                onClick={() => setShowDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={saving || !editing.target_name.trim()}
                className="gap-2"
              >
                {saving ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <CheckCircle2 className="h-4 w-4" />
                )}
                {editing.id ? 'Update' : 'Add'} Channel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── User Slack Mapping ─────────────────────────────── */}
      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Users className="h-5 w-5" />
            User → Slack Mapping
          </CardTitle>
          <CardDescription>
            Link local users to Slack accounts for automatic DM notifications when their assigned tasks change status.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <UserSlackMappingSection channels={channels} />
        </CardContent>
      </Card>
    </div>
  );
}

// ── User Slack Mapping Section ────────────────────────────────

function UserSlackMappingSection({ channels }: { channels: NotificationChannel[] }) {
  const [users, setUsers] = useState<UserSlackMapping[]>([]);
  const [loading, setLoading] = useState(true);
  const [slackUsers, setSlackUsers] = useState<{ id: string; name: string; real_name: string }[]>([]);
  const [fetchingSlack, setFetchingSlack] = useState(false);
  const [savingId, setSavingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Auto-detect bot_token from saved Slack notification channels
  const savedBotToken = channels
    .find((c) => c.channel_type === 'slack' && c.bot_token)
    ?.bot_token ?? '';

  const loadUsers = useCallback(async () => {
    try {
      const data = await userSlackMappingApi.list();
      setUsers(data);
    } catch {
      setError('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Auto-fetch Slack users when bot_token is available
  useEffect(() => {
    if (savedBotToken && slackUsers.length === 0 && !fetchingSlack) {
      fetchSlackUsersWithToken(savedBotToken);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedBotToken]);

  const fetchSlackUsersWithToken = async (token: string) => {
    if (!token.trim()) return;
    setFetchingSlack(true);
    setError(null);
    try {
      const data = await notificationChannelsApi.slackLookup(token);
      setSlackUsers(data.users.filter((u) => !u.is_bot));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch Slack users');
    } finally {
      setFetchingSlack(false);
    }
  };

  const updateMapping = async (userId: number, slackUserId: string) => {
    setSavingId(userId);
    try {
      await userSlackMappingApi.update(userId, slackUserId);
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, slack_user_id: slackUserId } : u))
      );
    } catch {
      setError('Failed to save mapping');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-6">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Token Status + Refresh */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 text-sm">
          {savedBotToken ? (
            <>
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <span className="text-muted-foreground">
                Using bot token from Slack notification channel
              </span>
            </>
          ) : (
            <>
              <XCircle className="h-4 w-4 text-amber-500" />
              <span className="text-muted-foreground">
                No Slack bot token found. Add a Slack notification channel above first.
              </span>
            </>
          )}
        </div>
        {savedBotToken && (
          <Button
            onClick={() => fetchSlackUsersWithToken(savedBotToken)}
            disabled={fetchingSlack}
            variant="outline"
            size="sm"
            className="gap-1.5"
          >
            {fetchingSlack ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Refresh Slack Users
          </Button>
        )}
      </div>

      {/* Mapping Table */}
      <div className="border rounded-lg divide-y">
        <div className="grid grid-cols-3 gap-4 px-4 py-2 bg-muted/50 text-xs font-medium text-muted-foreground uppercase tracking-wider">
          <span>Local User</span>
          <span>Slack User</span>
          <span>Actions</span>
        </div>
        {users.map((user) => (
          <div key={user.id} className="grid grid-cols-3 gap-4 px-4 py-3 items-center">
            <div className="flex items-center gap-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{user.username}</span>
              <Badge variant="outline" className="text-[10px] px-1.5">
                {user.role}
              </Badge>
            </div>
            <div>
              {slackUsers.length > 0 ? (
                <select
                  className="w-full h-9 rounded-md border bg-transparent px-3 text-sm"
                  value={user.slack_user_id}
                  onChange={(e) => updateMapping(user.id, e.target.value)}
                  disabled={savingId === user.id}
                >
                  <option value="">— Not linked —</option>
                  {slackUsers.map((su) => (
                    <option key={su.id} value={su.id}>
                      @{su.name} ({su.real_name})
                    </option>
                  ))}
                </select>
              ) : user.slack_user_id ? (
                <Badge variant="secondary" className="gap-1">
                  <Hash className="h-3 w-3" />
                  {user.slack_user_id}
                </Badge>
              ) : (
                <span className="text-sm text-muted-foreground">Not linked</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {savingId === user.id && <Loader2 className="h-4 w-4 animate-spin" />}
              {user.slack_user_id && savingId !== user.id && (
                <CheckCircle2 className="h-4 w-4 text-green-500" />
              )}
              {user.slack_user_id && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 px-2 text-destructive hover:text-destructive"
                  onClick={() => updateMapping(user.id, '')}
                  disabled={savingId === user.id}
                >
                  <X className="h-3.5 w-3.5" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {fetchingSlack && (
        <p className="text-xs text-muted-foreground text-center py-2">
          Loading Slack users...
        </p>
      )}
    </div>
  );
}
